import { Maximize2, X } from "lucide-react";
import { Suspense } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { DetailLoading } from "~/components/loading";
import {
  Button,
  Sheet,
  SheetBody,
  SheetContent,
  SheetHeader,
  SheetTitle,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "~/components/ui";
import { GroupDetail } from "./GroupDetail";

interface GroupPeekProps {
  id: string | null;
  onClose: () => void;
}

export function GroupPeek({ id, onClose }: GroupPeekProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const open = id !== null;

  const openFullPage = () => {
    if (!id) return;
    onClose();
    navigate(`/admin/groups/${id}`);
  };

  return (
    <Sheet open={open} onOpenChange={(next) => !next && onClose()}>
      <SheetContent side="right" showClose={false} className="flex flex-col p-0">
        <SheetHeader className="flex flex-row items-center justify-between gap-2">
          <SheetTitle className="capitalize">{t("model.group.modelName")}</SheetTitle>
          <div className="flex items-center gap-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={openFullPage}
                  aria-label={t("ui.openInFullPage")}
                >
                  <Maximize2 />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t("ui.openInFullPage")}</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button type="button" variant="ghost" size="icon" onClick={onClose} aria-label={t("ui.close")}>
                  <X />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t("ui.close")}</TooltipContent>
            </Tooltip>
          </div>
        </SheetHeader>
        <SheetBody>
          <Suspense fallback={<DetailLoading />}>
            {id !== null && <GroupDetail groupId={id} onBack={onClose} showUsers />}
          </Suspense>
        </SheetBody>
      </SheetContent>
    </Sheet>
  );
}
